// LE does not collect the BNBL v2 extra-guest surcharge when it is paid direct to the vendor
// (mirrors the instant purchase flow, where getPurchaseTotals excludes the surcharge for a
// surcharge_paid_direct_to_vendor offer). These helpers centralise that exclusion for the
// offer-page total and the per-item checkout charge.
//
// NOTE: the offer-level flag is not currently mapped from the offer response (removed in
// b79ee85530 — no live offer sets it), so `surchargePaidDirectToVendor` is always undefined today
// and these helpers default to LE-collected; svc-order still applies the flag server-side. The
// parameter is kept so the exclusion is ready the moment the flag is mapped again.

import { isFlashHotelType } from 'lib/offer/offerTypes'

/**
 * BNBL v2 (upfront extra-guest surcharge) is scoped to FLASH hotel offers only. svc-order accepts a
 * `bnbl_version=2` payload for the flash hotel type alone and rejects every other offer type
 * (tactical/always-on, villa, bedbank …), so the offer page must gate on the flag AND the flash
 * type. This is the single guard used for quoting, CTA state/display, and cart augmentation — a
 * non-flash offer therefore never quotes, never augments the cart, and keeps the legacy (v1) payload.
 */
export function isBnblV2FlashOffer(
  bnblV2Enabled: boolean,
  offerType: App.OfferType | undefined,
): boolean {
  return bnblV2Enabled && isFlashHotelType(offerType)
}

interface BnblRoomUpfrontSurcharge {
  applies: boolean
  total: number
  payableAtProperty: number
}

/**
 * Stamps the upfront extra-guest surcharge onto each BNBL cart item, index-aligned to `perRoom`
 * (same flat package→occupancy order the cart is built in). The surcharge field's presence is what
 * tags the order `bnbl_version=2` downstream, so it's set on every v2 item even at 0. When v2 is not
 * active (flag off OR non-flash offer) the items pass through untouched — the legacy payload.
 */
export function applyBnblUpfrontSurchargeToCheckoutItems(
  checkoutItems: Array<App.Checkout.BNBLLEHotelItem>,
  perRoom: Array<BnblRoomUpfrontSurcharge | undefined>,
  bnblV2FlashEnabled: boolean,
): Array<App.Checkout.BNBLLEHotelItem> {
  if (!bnblV2FlashEnabled) {
    return checkoutItems
  }
  return checkoutItems.map((checkoutItem, index) => {
    const roomSurcharge = perRoom[index]
    return {
      ...checkoutItem,
      upfrontExtraGuestSurcharge: roomSurcharge?.applies ? roomSurcharge.total : 0,
      upfrontExtraGuestSurchargePayableAtProperty: roomSurcharge?.applies ? roomSurcharge.payableAtProperty : 0,
    }
  })
}

export function leCollectsBnblExtraGuestSurcharge(
  surchargePaidDirectToVendor: boolean | undefined,
): boolean {
  return !surchargePaidDirectToVendor
}

/**
 * The upfront extra-guest surcharge LE actually charges for a BNBL v2 checkout item — 0 when the
 * surcharge is paid direct to the vendor. The full amount is still submitted to svc-order for its
 * stale-pricing check; this is only what LE collects.
 */
export function bnblUpfrontExtraGuestSurchargeCharge(
  upfrontExtraGuestSurcharge: number | undefined,
  surchargePaidDirectToVendor: boolean | undefined,
): number {
  if (!leCollectsBnblExtraGuestSurcharge(surchargePaidDirectToVendor)) {
    return 0
  }
  return upfrontExtraGuestSurcharge ?? 0
}

/** Whether the BNBL v2 extra-guest surcharge should be added to the offer-page displayed total. */
export function shouldShowBnblExtraGuestSurcharge(
  bnblV2Enabled: boolean,
  extraGuestSurchargeTotal: number,
  surchargePaidDirectToVendor: boolean | undefined,
): boolean {
  return (
    bnblV2Enabled &&
    extraGuestSurchargeTotal > 0 &&
    leCollectsBnblExtraGuestSurcharge(surchargePaidDirectToVendor)
  )
}
